import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { ElementType, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { animated, config, useSpringRef, useTransition } from 'react-spring';
import styled from 'styled-components';

import IconButton from 'atoms/buttons/IconButton';
import { Vertical } from 'atoms/layout/flex';
import { StepProps } from 'components/cardswap/types';
import { Background } from 'components/rewards/Background';
import { glossary } from 'lib/glossary';
import { tabletAndAbove } from 'style/mediaQuery';

import { useCardSwapContext } from '../useCardSwapContext';

const Actions = styled.div`
  position: absolute;
  right: var(--unit);
  top: var(--unit);
`;

const FullScreenBackground = styled(Background).attrs({ black: false })`
  height: 100%;
  width: 100%;
  overflow: auto;
  &::before {
    content: '';
    left: 0;
    right: 0;
    bottom: 0;
    position: absolute;
    height: calc(16 * var(--unit));
    background: linear-gradient(180deg, rgba(34, 36, 43, 0) 0%, #212121 53.12%);

    pointer-events: none;
    @media ${tabletAndAbove} {
      height: calc(18 * var(--unit));
    }
  }
`;
const ColumnLayout = styled(Vertical).attrs({ gap: 0, center: true })`
  height: 100%;
  width: 100%;
`;

const CardSwapStep = styled(animated.div)`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  & > * {
    flex: 1;
  }
`;

const Steps = styled.div`
  flex: 1;
  width: 100%;

  position: relative;
`;

export type Props = {
  steps: ElementType<StepProps>[];
  setOnCloseConfirmDialog: React.Dispatch<React.SetStateAction<boolean>>;
  cancelTrade: () => Promise<void>;
  completeTrade: StepProps['completeTrade'];
  onClose: StepProps['onClose'];
  useCardTradeItem?: boolean;
};

export const CardSwapFlow = ({
  steps,
  cancelTrade,
  completeTrade,
  onClose,
  setOnCloseConfirmDialog,
  useCardTradeItem,
}: Props) => {
  const ref = useSpringRef();
  const { formatMessage } = useIntl();
  const { step, tradeId, clearCardSwapState, nextStep } = useCardSwapContext();
  const transitions = useTransition(step, {
    ref,
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: config.stiff,
  });

  useEffect(() => {
    ref.start();
  }, [ref, step]);

  useEffect(() => {
    return () => clearCardSwapState();
  }, [clearCardSwapState]);

  if (!steps[step]) {
    clearCardSwapState();
    return null;
  }

  return (
    <FullScreenBackground text="SWAP">
      <ColumnLayout>
        <Steps>
          {transitions((styles, springStep) => {
            if (step !== springStep) return null;
            const Component = steps[springStep];

            return (
              <CardSwapStep style={styles}>
                <Component
                  nextStep={nextStep}
                  completeTrade={completeTrade}
                  onClose={onClose}
                  useCardTradeItem={useCardTradeItem}
                />
              </CardSwapStep>
            );
          })}
        </Steps>
      </ColumnLayout>
      <Actions>
        <IconButton
          onClick={() => {
            const moreSteps = step < steps.length - 1;
            if (tradeId && moreSteps) {
              setOnCloseConfirmDialog(true);
            } else if (moreSteps) {
              cancelTrade();
            } else {
              clearCardSwapState();
              onClose!();
            }
          }}
          color="tertiary"
          icon={faTimes}
          aria-label={formatMessage(glossary.close)}
        />
      </Actions>
    </FullScreenBackground>
  );
};
